import React, { useEffect, useState } from "react";
import { api, formatErr, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Database, Cloud, RefreshCw, Clock, CheckCircle2, Upload, ShieldAlert, Download } from "lucide-react";

export default function MyCompanyBackup() {
  const { companyId, currentCompany } = useAuth();
  const companyName = currentCompany?.name;
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const load = async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/companies/${companyId}/backups`);
      setItems(data.items || []);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [companyId]);

  const runBackup = async () => {
    if (!companyId) return toast.error("لم يتم تحديد الشركة");
    if (!confirm("سيتم إنشاء نسخة احتياطية لبيانات شركتك ورفعها للسحابة. متابعة؟")) return;
    setRunning(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/backup`);
      toast.success(`تم — ${(data.size_bytes/1024).toFixed(1)} KB${data.cloud_path ? " · مرفوعة للسحابة" : ""}`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRunning(false); }
  };

  const [restoring, setRestoring] = useState(false);
  const doRestore = async (file) => {
    if (!file || !companyId) return;
    if (!confirm(
      `تحذير هام:\n\n` +
      `ستُستبدل بيانات شركة "${companyName}" الحالية كاملةً ببيانات هذه النسخة الاحتياطية.\n\n` +
      `• لن تتأثر أي شركة أخرى في النظام إطلاقاً.\n` +
      `• ستُحذف المنتجات/الفواتير/العملاء الحاليون لهذه الشركة ثم يُعاد إنشاؤهم من الملف.\n\n` +
      `هل تريد المتابعة؟`
    )) return;
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/companies/${companyId}/restore`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({ detail: "فشل الاستعادة" }));
        throw new Error(err.detail || "فشل الاستعادة");
      }
      const data = await r.json();
      const restored = Object.values(data.stats?.restored || {}).reduce((a, b) => a + b, 0);
      toast.success(`تمت الاستعادة — أُعيد ${restored} سجل عبر ${Object.keys(data.stats?.restored || {}).length} مجموعة`);
      load();
    } catch (e) { toast.error(e.message || String(e)); }
    finally { setRestoring(false); }
  };

  const [restoringId, setRestoringId] = useState(null);
  const doRestoreFromBackup = async (b) => {
    if (!companyId) return;
    if (!confirm(
      `تحذير هام:\n\n` +
      `ستُستعاد بيانات شركة "${companyName}" من النسخة:\n${b.filename}\n` +
      `${b.cloud_path ? "(من السحابة)" : "(محلياً)"}\n\n` +
      `• ستُحذف بيانات هذه الشركة الحالية ثم يُعاد إنشاؤها من هذه النسخة.\n` +
      `• لن تتأثر أي شركة أخرى إطلاقاً.\n\nهل تريد المتابعة؟`
    )) return;
    setRestoringId(b.id);
    try {
      const { data } = await api.post(`/companies/${companyId}/restore/${b.id}`);
      const restored = Object.values(data.stats?.restored || {}).reduce((a, c) => a + c, 0);
      toast.success(`تمت الاستعادة ${data.from === "cloud" ? "من السحابة" : "محلياً"} — أُعيد ${restored} سجل`);
      load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setRestoringId(null); }
  };

  const doDownload = async (b) => {
    try {
      const token = localStorage.getItem("access_token");
      const r = await fetch(`${API}/companies/${companyId}/backups/${b.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) throw new Error("فشل تحميل النسخة");
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = b.filename;
      a.click();
      toast.success(`تم بدء تحميل ${b.filename}`);
    } catch (e) { toast.error(e.message || String(e)); }
  };

  return (
    <div className="space-y-6" data-testid="my-backup-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2">
            <Database className="w-7 h-7 text-blue-600" /> النسخ الاحتياطي لشركتي
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            أنشئ نسخة كاملة من بيانات شركة <b>{companyName || "—"}</b> واحتفظ بها على السحابة.
          </p>
        </div>
        <Button onClick={runBackup} disabled={running || !companyId} className="gap-2 bg-blue-600 hover:bg-blue-700" data-testid="my-backup-run-btn">
          <RefreshCw className={`w-4 h-4 ${running ? "animate-spin" : ""}`} />
          {running ? "جارٍ الإنشاء..." : "إنشاء نسخة احتياطية الآن"}
        </Button>
      </div>

      <Card className="p-4 bg-gradient-to-br from-rose-50 to-orange-50 border-rose-200">
        <div className="flex items-start gap-3">
          <ShieldAlert className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
          <div className="flex-1 text-sm text-slate-800 leading-relaxed">
            <b className="text-rose-900">استعادة من ملف نسخة احتياطية:</b> ارفع ملف <code className="font-mono text-xs bg-white px-1 rounded">.json.gz</code> لهذه الشركة فقط.
            سيتم استبدال بيانات هذه الشركة الحالية بالكامل — <b>ولن تتأثر أي شركة أخرى.</b>
            <div className="mt-2 flex items-center gap-2">
              <input type="file" accept=".gz,application/gzip" id="restore-file" hidden onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) doRestore(f);
                e.target.value = "";
              }} data-testid="restore-file-input" />
              <Button
                size="sm"
                variant="outline"
                disabled={restoring || !companyId}
                onClick={() => document.getElementById("restore-file").click()}
                className="gap-1 border-rose-400 text-rose-700 hover:bg-rose-50"
                data-testid="restore-btn"
              >
                <Upload className={`w-3.5 h-3.5 ${restoring ? "animate-pulse" : ""}`} /> {restoring ? "جارٍ الاستعادة..." : "استعادة من ملف"}
              </Button>
              <span className="text-xs text-rose-800">يُسمح فقط بنسخ هذه الشركة نفسها (تحقق تلقائي)</span>
            </div>
          </div>
        </div>
      </Card>

      <Card className="p-4 bg-gradient-to-br from-blue-50 to-emerald-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Cloud className="w-6 h-6 text-blue-600 shrink-0 mt-0.5" />
          <div className="text-sm text-slate-800 leading-relaxed">
            <b className="text-blue-900">ماذا يتم نسخه؟</b> جميع بيانات شركتك: المنتجات، العملاء، الموردون، الفواتير، السندات،
            الصناديق، القيود، الحسابات، المستودعات، المجموعات، الشيكات، الحملات، وسجل التدقيق.
            <br />
            <b className="text-emerald-900">أين تُحفظ؟</b> على خوادم Emergent السحابية بشكل آمن ومنفصل عن أي شركة أخرى.
          </div>
        </div>
      </Card>

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <div className="p-3 border-b bg-muted/30 font-heading font-bold flex items-center gap-2">
          <Clock className="w-4 h-4" /> سجل النسخ الاحتياطي ({items.length})
        </div>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="text-start">التاريخ</TableHead>
              <TableHead className="text-start">اسم الملف</TableHead>
              <TableHead className="text-start">الحجم</TableHead>
              <TableHead className="text-start">السحابة</TableHead>
              <TableHead className="text-start">الحالة</TableHead>
              <TableHead className="text-start">تحميل</TableHead>
              <TableHead className="text-start">استعادة</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-6 text-muted-foreground">جارٍ التحميل...</TableCell></TableRow>}
            {!loading && items.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                لا توجد نسخ بعد — اضغط "إنشاء نسخة احتياطية الآن"
              </TableCell></TableRow>
            )}
            {items.map(b => (
              <TableRow key={b.id} data-testid={`backup-row-${b.id}`}>
                <TableCell className="text-xs">{new Date(b.created_at).toLocaleString("ar-EG")}</TableCell>
                <TableCell className="font-mono text-xs" dir="ltr">{b.filename}</TableCell>
                <TableCell className="font-mono text-xs">{(b.size_bytes/1024).toFixed(1)} KB</TableCell>
                <TableCell>
                  {b.cloud_path ? (
                    <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><Cloud className="w-3.5 h-3.5" /> مرفوعة</span>
                  ) : <span className="text-xs text-muted-foreground">—</span>}
                </TableCell>
                <TableCell>
                  <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> {b.status || "ok"}</span>
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => doDownload(b)}
                    className="gap-1 border-blue-400 text-blue-700 hover:bg-blue-50 h-7"
                    data-testid={`download-backup-${b.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> {b.cloud_path ? "من السحابة" : "محلي"}
                  </Button>
                </TableCell>
                <TableCell>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={restoringId === b.id}
                    onClick={() => doRestoreFromBackup(b)}
                    className="gap-1 border-rose-400 text-rose-700 hover:bg-rose-50 h-7"
                    data-testid={`restore-backup-${b.id}`}
                  >
                    <RefreshCw className={`w-3.5 h-3.5 ${restoringId === b.id ? "animate-spin" : ""}`} /> {restoringId === b.id ? "جارٍ..." : (b.cloud_path ? "من السحابة" : "استعادة")}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
